import React from 'react';
import {
  array, arrayOf, bool, number, object, objectOf, shape, string
} from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { copy } from '@thd-olt-functional/utils';

function getDimension(suppDimensions, entryGroup, dimension) {
  const entry = suppDimensions.filter(({ specTitle }) => specTitle === entryGroup);
  let spec;
  if (entry.length) {
    spec = entry[0].specifications
      .sort((specA, specB) => {
        if (specA.specName < specB.specName) {
          return -1;
        }
        if (specA.specName > specB.specName) {
          return 1;
        }
        return 0;
      })
      .find(({ specName = '' }) => specName.toLowerCase().indexOf(dimension) > -1) || {};
  }
  return spec?.specValue || '';
}

const ProductStructuredData = ({
  aggregateReviews,
  availabilityType,
  identifiers,
  media,
  details,
  reviews,
  pricing,
  specifications
}) => {
  const {
    itemId,
    productLabel = '',
    brandName = '',
    upcGtin13 = '',
    storeSkuNumber = '',
    canonicalUrl = ''
  } = identifiers || {};
  const { value, promotion } = pricing || {};
  const { description } = details || {};
  const today = new Date(Date.now());

  const specs = copy(specifications || []).data;

  const schemaString = JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: productLabel,
    image: media?.images?.map(({ url }) => url.replace('<SIZE>', '100')),
    description,
    productID: itemId,
    sku: storeSkuNumber,
    gtin13: upcGtin13,
    depth: getDimension(specs, 'Dimensions', 'depth'),
    height: getDimension(specs, 'Dimensions', 'height'),
    width: getDimension(specs, 'Dimensions', 'width'),
    color: getDimension(specs, 'Details', 'color'),
    weight: getDimension(specs, 'Details', 'weight'),
    brand: {
      '@type': 'Thing',
      name: brandName,
    },
    ...(Number(aggregateReviews?.totalReviews) && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: aggregateReviews?.averageRating,
        reviewCount: aggregateReviews?.totalReviews,
      },
    }),
    offers: {
      '@type': 'Offer',
      url: `https://www.homedepot.com${canonicalUrl}`,
      priceCurrency: 'USD',
      price: value,
      priceValidUntil:
        (promotion && promotion?.dates?.end) || `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`,
      availability: availabilityType?.status ? 'https://schema.org/InStock' : undefined,
    },
    review: (reviews || []).map((review) => ({
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.Rating,
        bestRating: '5',
      },
      author: {
        '@type': 'Person',
        name: review.UserNickname,
      },
      headline: review.Title,
      reviewBody: review.ReviewText,
    })),
  });
  return (
    <Helmet>
      <script type="application/ld+json">{schemaString}</script>
    </Helmet>
  );
};

ProductStructuredData.propTypes = {
  reviews: arrayOf(shape({})),
  availabilityType: shape({
    discontinued: bool,
    status: bool
  }),
  aggregateReviews: objectOf(string),
  identifiers: shape({
    itemId: string,
    productLabel: string,
    brandName: string,
    upcGtin13: string,
    storeSkuNumber: string,
    canonicalUrl: string,
  }),
  media: shape({
    // eslint-disable-next-line
    images: arrayOf(object)
  }),
  details: shape({
    description: string,
    // eslint-disable-next-line
    descriptiveAttributes: array
  }),
  pricing: shape({
    value: number,
    promotion: shape({
      isCartLevelPromotion: bool,
      dates: shape({
        end: string
      })
    })
  }),
  specifications: arrayOf(shape({}))
};

ProductStructuredData.defaultProps = {
  availabilityType: {},
  reviews: [],
  aggregateReviews: {},
  identifiers: {},
  media: {},
  details: {},
  pricing: {},
  specifications: []
};

export { ProductStructuredData };
